import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../../firebase';
import { TextInput, Alert } from 'flowbite-react';
import { ChevronLeft } from 'lucide-react';
import { sendPasswordResetWithCustomRedirect } from '../../services/authService';

const AppForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {
      await sendPasswordResetWithCustomRedirect(email);
      setResetEmailSent(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen relative overflow-hidden bg-[#0A0A0A]">
      {/* Back Button */}
      <div className="absolute top-0 left-0 z-10" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)', paddingLeft: '1rem' }}>
        <Link
          to="/app/login"
          className="inline-flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 bg-white/10 hover:bg-white/20"
        >
          <ChevronLeft className="text-white" size={20} />
        </Link>
      </div>

      {/* Background Image with Gradient Overlay */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Background Image Layer */}
        <img
          src="/images/headerbg.png"
          alt=""
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[1600px] max-w-none h-full object-cover opacity-30 object-top"
        />
        {/* Primary Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] via-[#111111]/95 to-[#111111]" />
        {/* Accent Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-emerald-500/[0.03] to-transparent" />
      </div>

      {/* Gradient Orbs */}
      <div className="fixed top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-3xl -translate-x-1/2 bg-emerald-500/[0.07]" />
      <div className="fixed bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl translate-x-1/2 bg-emerald-500/[0.07]" />

      {/* Content */}
      <div className="relative flex flex-col items-center justify-center flex-1 px-8" 
           style={{ paddingTop: 'calc(env(safe-area-inset-top) + 2rem)' }}>
        <div className="w-full max-w-sm bg-transparent">
          <div>
            {/* Logo */}
            <div className="flex justify-center mb-12">
              <div className="w-32 h-32">
                <img
                  src="/images/ummahti_white_transparent.png"
                  alt="Ummahti"
                  className="w-full transition-opacity duration-300 opacity-90"
                />
              </div>
            </div>

            <h2 className="text-2xl font-light mb-4 text-center text-white">
              Reset your password
            </h2>
            
            <p className="text-center mb-8 text-gray-400">
              Enter your email address and we'll send you a link to reset your password
            </p>
            
            {error && (
              <Alert 
                color="failure" 
                className="mb-6"
                onDismiss={() => setError(null)}
              >
                {error}
              </Alert>
            )}
            
            {resetEmailSent ? (
              <div className="text-center">
                <Alert 
                  color="success" 
                  className="mb-6"
                >
                  Password reset email sent. Please check your inbox.
                </Alert>
                <p className="mt-4 text-gray-400">
                  Didn't receive an email? Check your spam folder or try again.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => navigate('/app/login')}
                    className="w-full py-4 px-6 rounded-2xl backdrop-blur-md transition-all duration-300 bg-white/10 hover:bg-white/20 text-white border border-white/[0.05]"
                  >
                    Back to login
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <TextInput
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="[&>input]:rounded-2xl [&>input]:px-6 [&>input]:py-4 [&>input]:backdrop-blur-md"
                  />
                </div>
                <button 
                  type="submit" 
                  className="w-full py-4 px-6 rounded-2xl backdrop-blur-md transition-all duration-300 bg-emerald-500 hover:bg-emerald-400 text-black font-medium"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send reset link'}
                </button>
                <div className="mt-4 text-center">
                  <button 
                    onClick={() => navigate('/app/login')}
                    className="text-sm text-emerald-400 hover:text-emerald-300"
                  >
                    Back to login
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer Text */}
      <div className="relative pb-8 px-8 text-center" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)' }}>
        <p className="text-sm text-gray-400">
          By continuing, you agree to our{' '}
          <Link 
            to="/app/terms" 
            className="text-emerald-400 hover:text-emerald-300"
          >
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link 
            to="/app/privacy" 
            className="text-emerald-400 hover:text-emerald-300"
          >
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AppForgotPassword; 